import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronUp,
  ChevronDown,
  Clock,
  CreditCard,
  RefreshCcw,
  Percent,
} from 'lucide-react';
import { CommissionContractOfferApi } from '../../api/CommissionContractAPI';
import { CardContent, CardV2 } from '../CardV2/CardV2';
import { RadioGroup, RadioGroupItem } from '../RadioGroup/RadioGroup';
import { LabelV2 } from '../LabelV2/LabelV2';
import { Badge } from '../Badge/Badge';
import { ButtonV2 } from '../ButtonV2/ButtonV2';

interface CommissionContractOfferPickerProps {
  offers: CommissionContractOfferApi[];
  selectedOfferId: number | null;
  onSelectOffer: (id: number) => void;
}

export default function CommissionContractOfferPicker({
  offers,
  selectedOfferId,
  onSelectOffer,
}: CommissionContractOfferPickerProps) {
  const { t } = useTranslation();
  const [expandedOfferId, setExpandedOfferId] = useState<number | null>(null);

  const toggleOfferExpansion = (id: number) => {
    setExpandedOfferId(expandedOfferId === id ? null : id);
  };

  const selectableOffers = offers.filter((o) => o.id != null);

  if (selectableOffers.length === 0) {
    return null;
  }

  return (
    <RadioGroup
      value={selectedOfferId?.toString() ?? ''}
      onValueChange={(value) => onSelectOffer(parseInt(value, 10))}
      className="space-y-4 w-full"
    >
      {selectableOffers.map((offer) => {
        const offerId = offer.id!;
        const isSelected = offerId === selectedOfferId;
        const hasListingFee = (offer.dropFeePrice ?? 0) > 0;
        const hasRenewalFee = (offer.renewalFeePrice ?? 0) > 0;

        return (
          <CardV2
            key={offerId}
            className={`w-full overflow-hidden transition-all duration-200 ${
              isSelected ? 'ring-2 ring-primary' : ''
            }`}
          >
            <CardContent className="p-0">
              <div className="flex items-center justify-between p-4 gap-2">
                <div className="flex items-center space-x-3 flex-grow min-w-0">
                  <RadioGroupItem value={offerId.toString()} id={`commission-offer-${offerId}`} />
                  <LabelV2
                    htmlFor={`commission-offer-${offerId}`}
                    className="font-medium cursor-pointer truncate"
                  >
                    {offer.name}
                  </LabelV2>
                </div>
                <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
                  {hasListingFee ? (
                    <Badge variant="default">
                      {`${offer.dropFeeCurrency || ''} ${offer.dropFeePrice}`.trim()}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">{t('bookings.listing.free') || 'Free'}</Badge>
                  )}
                  <ButtonV2
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleOfferExpansion(offerId)}
                  >
                    {expandedOfferId === offerId ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </ButtonV2>
                </div>
              </div>
              <AnimatePresence>
                {expandedOfferId === offerId && (
                  <motion.div
                    key={`commission-offer-expanded-${offerId}`}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-4 pb-4"
                  >
                    <div className="text-sm text-muted-foreground space-y-2 max-w-full">
                      {offer.customerDescription && (
                        <div
                          className="break-words prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: offer.customerDescription }}
                        />
                      )}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                        <div className="flex items-center">
                          <Percent className="h-4 w-4 mr-1 flex-shrink-0" />
                          <span>
                            {t('commission_contract_page.form.offer_split') || 'Split (store / you)'}:{' '}
                            {offer.salonSplitPercentage ?? '—'}% / {offer.brideSplitPercentage ?? '—'}%
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1 flex-shrink-0" />
                          <span>
                            {t('commission_contract_page.form.offer_duration') || 'Time in salon (days)'}:{' '}
                            {offer.saleDuration}
                          </span>
                        </div>
                      </div>
                      {(hasListingFee || hasRenewalFee) && (
                        <div>
                          {hasListingFee && (
                            <div className="flex items-center">
                              <CreditCard className="h-4 w-4 mr-1 flex-shrink-0" />
                              <span>
                                {t('commission_contract_page.form.fee_info.drop_fee') || 'Listing fee:'}{' '}
                                {offer.dropFeePrice} {offer.dropFeeCurrency || ''}
                              </span>
                            </div>
                          )}
                          {hasRenewalFee && (
                            <div className="flex items-center">
                              <RefreshCcw className="h-4 w-4 mr-1 flex-shrink-0" />
                              <span>
                                {t('commission_contract_page.form.fee_info.renewal_fee') || 'Renewal fee:'}{' '}
                                {offer.renewalFeePrice} {offer.renewalFeeCurrency || ''}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </CardV2>
        );
      })}
    </RadioGroup>
  );
}
